/**
 * Chat Cache Hook
 *
 * Writes the messenger's live state through to the message cache, so the
 * next time the chat opens it starts from where this one left off: the
 * sidebar as the user last saw it (new messages, read ticks, created and
 * deleted chats included) and the newest page of whichever conversation was
 * open. Reading the cache back happens where the fetches are — the
 * conversation list in useWebSocket, a conversation's messages in
 * ChatInterface.
 */

import { useEffect } from 'react';
import { writeCachedConversations, writeCachedMessages } from '../cache/messageCache';

// State changes in bursts (a page load, a typing storm of receipts); one
// write per burst is plenty for a cache that only decides the first frame.
const WRITE_DELAY_MS = 400;

export const useChatCache = ({ currentUser, conversations, selectedConversation, messages, loading }) => {
  const username = currentUser?.username;
  const conversationId = selectedConversation?.id;

  useEffect(() => {
    // An empty list is what the state holds before the first fetch, not a
    // fact about the user; never let it wipe a real one.
    if (!username || !Array.isArray(conversations) || conversations.length === 0) return undefined;
    const timer = setTimeout(() => writeCachedConversations(username, conversations), WRITE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [username, conversations]);

  useEffect(() => {
    if (!username || !conversationId || loading || !Array.isArray(messages)) return undefined;
    // Between selecting a conversation and its page arriving, `messages` still
    // belongs to the previous one. `loading` covers that window; this is the
    // belt to its braces.
    const foreign = messages.some((m) => m.conversationId && m.conversationId !== conversationId);
    if (foreign) return undefined;
    const timer = setTimeout(() => writeCachedMessages(username, conversationId, messages), WRITE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [username, conversationId, messages, loading]);
};

export default useChatCache;
